<template>
  <!-- Режим встраивания: только карточки загрузки и списка (без страницы, заголовка и блока «Место на сервере») -->
  <div v-if="embedMode" class="admin-video-embed">
    <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Загрузить видео</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-select label="Раздел образования" label-placement="stacked" v-model="uploadSectionKey"
              interface="popover" mode="ios" placeholder="Выберите раздел">
              <ion-select-option v-for="s in educationSections" :key="s.folderKey" :value="s.folderKey">{{ s.name }} ({{ s.folderKey }})</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item>
            <ion-input label="Название для пользователей" label-placement="stacked" v-model="uploadDisplayName"
              placeholder="Отображаемое название видео"></ion-input>
          </ion-item>
          <ion-item>
            <input type="file" ref="fileInput" accept="video/*" @change="onFileSelected" />
          </ion-item>
          <p v-if="selectedFile" class="file-info">
            Файл: {{ selectedFile.name }} ({{ formatBytes(selectedFile.size) }})
            <span v-if="diskSpace && selectedFile.size > diskSpace.availableBytes" class="file-too-big">
              — недостаточно места на сервере
            </span>
            <span v-else-if="selectedFile.size > LARGE_FILE_WARN_BYTES" class="file-chunked-hint">
              — будет загружаться по частям (чанковая загрузка)
            </span>
          </p>
          <ion-progress-bar v-if="uploadInProgress" :value="uploadProgressPercent / 100" type="determinate"></ion-progress-bar>
          <p v-if="uploadInProgress" class="upload-progress-text">{{ uploadProgressPercent }}%</p>
          <p v-if="uploadInProgress" class="upload-warning">⚠️ Не закрывайте окно до завершения загрузки.</p>
          <ion-button expand="block" @click="doUpload()" :disabled="!canUpload || uploadInProgress">
            {{ uploadInProgress ? 'Загрузка…' : 'Загрузить на сервер' }}
          </ion-button>
        </ion-card-content>
      </ion-card>
      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Видео раздела «Образование»</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="hasProcessingVideos" class="info-hint">ℹ️ Конвертация видео идёт на сервере. Можно закрыть окно и вернуться позднее — прогресс обновится автоматически.</p>
          <ion-item>
            <ion-select label="Раздел" label-placement="stacked" v-model="filterSectionKey" interface="popover" mode="ios"
              @ionChange="loadVideos()">
              <ion-select-option value="">Все разделы</ion-select-option>
              <ion-select-option v-for="s in educationSections" :key="s.folderKey" :value="s.folderKey">{{ s.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-list v-if="videos.length">
            <ion-item v-for="v in videos" :key="v.id" class="video-row">
              <ion-label>
                <h2>{{ v.displayName }}</h2>
                <p>{{ v.sectionName || v.sectionFolderKey }} · {{ effectiveStatusLabel(v) }} · {{ v.sourceType }}</p>
                <ion-progress-bar v-if="isProcessingWithProgress(v)" :value="(conversionProgress[Number(v.id)]?.percent ?? 0) / 100" type="determinate" class="conversion-progress"></ion-progress-bar>
              </ion-label>
              <ion-badge :color="statusColor(effectiveStatus(v))">{{ effectiveStatusLabel(v) }}</ion-badge>
              <div class="video-actions" slot="end">
                <ion-button fill="clear" color="medium" size="small" @click="renameVideo(v)" title="Переименовать">
                  <ion-icon slot="icon-only" :icon="pencilOutline"></ion-icon>
                </ion-button>
                <ion-button v-if="v.status === 'PROCESSING'" fill="clear" color="warning" size="small" @click="cancelConversion(v)">
                  <ion-icon slot="icon-only" :icon="stopCircleOutline"></ion-icon>
                </ion-button>
                <ion-button v-if="v.status === 'FAILED' || v.status === 'CANCELLED'" fill="clear" color="primary" size="small" @click="retryConversion(v)">
                  <ion-icon slot="icon-only" :icon="refreshOutline"></ion-icon>
                </ion-button>
                <ion-button v-if="v.status === 'READY' || v.status === 'FAILED' || v.status === 'CANCELLED'" fill="clear" color="danger" size="small" @click="deleteVideo(v)">
                  <ion-icon slot="icon-only" :icon="trashOutline"></ion-icon>
                </ion-button>
              </div>
            </ion-item>
          </ion-list>
          <p v-else class="empty-hint">Нет видео или загрузка не реализована.</p>
        </ion-card-content>
      </ion-card>
  </div>
  <ion-page v-else>
    <PageHeaderWithMenu title="Загрузка видео" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card disk-card">
        <ion-card-header>
          <ion-card-title>Место на сервере</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="diskSpace">Свободно: {{ formatBytes(diskSpace.availableBytes) }} из {{ formatBytes(diskSpace.totalBytes) }}</p>
          <p v-else class="ion-text-wrap">Загрузка данных о месте…</p>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Загрузить видео</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-select label="Раздел образования" label-placement="stacked" v-model="uploadSectionKey"
              interface="popover" mode="ios" placeholder="Выберите раздел">
              <ion-select-option v-for="s in educationSections" :key="s.folderKey" :value="s.folderKey">{{ s.name }} ({{ s.folderKey }})</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item>
            <ion-input label="Название для пользователей" label-placement="stacked" v-model="uploadDisplayName"
              placeholder="Отображаемое название видео"></ion-input>
          </ion-item>
          <ion-item>
            <input type="file" ref="fileInput" accept="video/*" @change="onFileSelected" />
          </ion-item>
          <p v-if="selectedFile" class="file-info">
            Файл: {{ selectedFile.name }} ({{ formatBytes(selectedFile.size) }})
            <span v-if="diskSpace && selectedFile.size > diskSpace.availableBytes" class="file-too-big">
              — недостаточно места на сервере
            </span>
            <span v-else-if="selectedFile.size > LARGE_FILE_WARN_BYTES" class="file-chunked-hint">
              — будет загружаться по частям (чанковая загрузка)
            </span>
          </p>
          <ion-progress-bar v-if="uploadInProgress" :value="uploadProgressPercent / 100" type="determinate"></ion-progress-bar>
          <p v-if="uploadInProgress" class="upload-progress-text">{{ uploadProgressPercent }}%</p>
          <p v-if="uploadInProgress" class="upload-warning">⚠️ Не закрывайте окно до завершения загрузки.</p>
          <ion-button expand="block" @click="doUpload()" :disabled="!canUpload || uploadInProgress">
            {{ uploadInProgress ? 'Загрузка…' : 'Загрузить на сервер' }}
          </ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Видео раздела «Образование»</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="hasProcessingVideos" class="info-hint">ℹ️ Конвертация видео идёт на сервере. Можно закрыть окно и вернуться позднее — прогресс обновится автоматически.</p>
          <ion-item>
            <ion-select label="Раздел" label-placement="stacked" v-model="filterSectionKey" interface="popover" mode="ios"
              @ionChange="loadVideos()">
              <ion-select-option value="">Все разделы</ion-select-option>
              <ion-select-option v-for="s in educationSections" :key="s.folderKey" :value="s.folderKey">{{ s.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-list v-if="videos.length">
            <ion-item v-for="v in videos" :key="v.id" class="video-row">
              <ion-label>
                <h2>{{ v.displayName }}</h2>
                <p>{{ v.sectionName || v.sectionFolderKey }} · {{ effectiveStatusLabel(v) }} · {{ v.sourceType }}</p>
                <ion-progress-bar v-if="isProcessingWithProgress(v)" :value="(conversionProgress[Number(v.id)]?.percent ?? 0) / 100" type="determinate" class="conversion-progress"></ion-progress-bar>
              </ion-label>
              <ion-badge :color="statusColor(effectiveStatus(v))">{{ effectiveStatusLabel(v) }}</ion-badge>
              <div class="video-actions" slot="end">
                <!-- Кнопка переименования -->
                <ion-button fill="clear" color="medium" size="small" @click="renameVideo(v)" title="Переименовать">
                  <ion-icon slot="icon-only" :icon="pencilOutline"></ion-icon>
                </ion-button>
                <!-- Кнопка отмены конвертации (для PROCESSING) -->
                <ion-button v-if="v.status === 'PROCESSING'" fill="clear" color="warning" size="small" @click="cancelConversion(v)">
                  <ion-icon slot="icon-only" :icon="stopCircleOutline"></ion-icon>
                </ion-button>
                <!-- Кнопка повтора конвертации (для FAILED/CANCELLED) -->
                <ion-button v-if="v.status === 'FAILED' || v.status === 'CANCELLED'" fill="clear" color="primary" size="small" @click="retryConversion(v)">
                  <ion-icon slot="icon-only" :icon="refreshOutline"></ion-icon>
                </ion-button>
                <!-- Кнопка удаления (для READY/FAILED/CANCELLED) -->
                <ion-button v-if="v.status === 'READY' || v.status === 'FAILED' || v.status === 'CANCELLED'" fill="clear" color="danger" size="small" @click="deleteVideo(v)">
                  <ion-icon slot="icon-only" :icon="trashOutline"></ion-icon>
                </ion-button>
              </div>
            </ion-item>
          </ion-list>
          <p v-else class="empty-hint">Нет видео или загрузка не реализована.</p>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { EducationSection } from '@/model/education-section.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { IDiskSpace, IEducationVideo, VideoProcessingStatus } from '@/model/video-upload.model';
import AdminService from '@/services/admin.service';
import { subscribeToConversionProgress } from '@/services/video-conversion-progress.service';
import {
  alertController,
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonProgressBar,
  IonSelect,
  IonSelectOption,
  toastController,
} from '@ionic/vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { pencilOutline, refreshOutline, stopCircleOutline, trashOutline } from 'ionicons/icons';
import { defineComponent, inject, ref } from 'vue';

export default defineComponent({
  name: 'AdminVideoUpload',
  components: {
    IonBadge,
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonProgressBar,
    IonSelect,
    IonSelectOption,
    PageHeaderWithMenu,
  },
  props: {
    embedMode: { type: Boolean, default: false },
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    const fileInput = ref<HTMLInputElement | null>(null);
    return {
      isMobile: globalContext.isMobile,
      fileInput,
      trashOutline,
      stopCircleOutline,
      refreshOutline,
      pencilOutline,
    };
  },
  data() {
    return {
      /** Порог (2 ГБ): выше — показываем предупреждение о возможном сбое загрузки (см. LARGE_FILE_UPLOAD.md). */
      LARGE_FILE_WARN_BYTES: 2 * 1024 * 1024 * 1024,
      adminService: new AdminService(),
      diskSpace: null as IDiskSpace | null,
      educationSections: [] as EducationSection[],
      videos: [] as IEducationVideo[],
      uploadSectionKey: '' as string,
      uploadDisplayName: '',
      selectedFile: null as File | null,
      filterSectionKey: '',
      uploadInProgress: false,
      uploadProgressPercent: 0,
      uploadCancelled: false,
      currentUploadId: null as string | null,
      conversionProgress: {} as Record<number, { percent: number; status: string }>,
      progressUnsubscribers: {} as Record<number, () => void>,
      headerScrolled: false,
    };
  },
  computed: {
    canUpload(): boolean {
      if (!this.selectedFile || !this.uploadSectionKey || !this.uploadDisplayName?.trim()) return false;
      if (this.diskSpace && this.selectedFile.size > this.diskSpace.availableBytes) return false;
      return true;
    },
    hasProcessingVideos(): boolean {
      return this.videos.some((v) => v.status === 'PROCESSING');
    },
  },
  mounted() {
    this.loadDiskSpace();
    this.loadSections();
    this.loadVideos();
  },
  beforeUnmount() {
    Object.values(this.progressUnsubscribers).forEach((fn) => fn());
    this.progressUnsubscribers = {};
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    formatBytes(bytes: number): string {
      if (bytes === 0) return '0 Б';
      const k = 1024;
      const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
    },
    statusLabel(s: VideoProcessingStatus): string {
      const map: Record<VideoProcessingStatus, string> = {
        UPLOADED: 'Загружено',
        PROCESSING: 'В обработке',
        READY: 'Готово',
        FAILED: 'Ошибка',
        CANCELLED: 'Отменено',
      };
      return map[s] || s;
    },
    statusColor(s: VideoProcessingStatus): string {
      const map: Record<VideoProcessingStatus, string> = {
        UPLOADED: 'warning',
        PROCESSING: 'primary',
        READY: 'success',
        FAILED: 'danger',
        CANCELLED: 'medium',
      };
      return map[s] || 'medium';
    },
    onFileSelected(ev: Event) {
      const input = ev.target as HTMLInputElement;
      this.selectedFile = input.files?.[0] || null;
    },
    async loadDiskSpace() {
      try {
        this.diskSpace = await this.adminService.getDiskSpace();
      } catch (e) {
        console.error('loadDiskSpace', e);
      }
    },
    async loadSections() {
      try {
        this.educationSections = await this.adminService.getEducationSections();
        if (this.educationSections.length && !this.uploadSectionKey) {
          this.uploadSectionKey = this.educationSections[0].folderKey;
        }
      } catch (e) {
        console.error('loadSections', e);
      }
    },
    effectiveStatus(v: IEducationVideo): VideoProcessingStatus {
      const prog = this.conversionProgress[Number(v.id)];
      if (prog?.status === 'READY' || prog?.status === 'FAILED') return prog.status as VideoProcessingStatus;
      return v.status;
    },
    effectiveStatusLabel(v: IEducationVideo): string {
      return this.statusLabel(this.effectiveStatus(v));
    },
    isProcessingWithProgress(v: IEducationVideo): boolean {
      const videoId = Number(v.id);
      return v.status === 'PROCESSING' && this.conversionProgress[videoId] != null;
    },
    async loadVideos() {
      try {
        const prev = this.videos;
        this.videos = await this.adminService.getEducationVideos(
          this.filterSectionKey || undefined
        );
        const processingIds = new Set((this.videos as IEducationVideo[]).filter((v) => v.status === 'PROCESSING').map((v) => Number(v.id)));
        const prevProcessingIds = new Set((prev || []).filter((v) => v.status === 'PROCESSING').map((v) => Number(v.id)));
        prevProcessingIds.forEach((id) => {
          if (!processingIds.has(id)) {
            const unsub = this.progressUnsubscribers[id];
            if (unsub) unsub();
            delete this.progressUnsubscribers[id];
          }
        });
        processingIds.forEach((videoId) => {
          if (this.progressUnsubscribers[videoId]) return;
          const unsub = subscribeToConversionProgress(videoId, (data) => {
            const id = Number(data.videoId);
            this.conversionProgress = {
              ...this.conversionProgress,
              [id]: { percent: data.percent, status: data.status },
            };
            if (data.status === 'READY' || data.status === 'FAILED') {
              this.loadVideos();
            }
          });
          this.progressUnsubscribers[videoId] = unsub;
        });
      } catch (e) {
        console.error('loadVideos', e);
      }
    },
    async doUpload() {
      if (!this.canUpload || !this.selectedFile) return;
      this.uploadInProgress = true;
      this.uploadProgressPercent = 0;
      this.uploadCancelled = false;
      this.currentUploadId = null;
      try {
        await this.adminService.uploadEducationVideoWithCallback(
          this.selectedFile,
          this.uploadSectionKey,
          this.uploadDisplayName.trim(),
          (uploadedBytes: number, totalBytes: number) => {
            this.uploadProgressPercent = totalBytes ? Math.round((100 * uploadedBytes) / totalBytes) : 0;
          },
          (uploadId: string) => {
            this.currentUploadId = uploadId;
          },
          () => this.uploadCancelled
        );
        const toast = await toastController.create({
          message: 'Видео загружено. Статус: В обработке.',
          duration: 3000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.uploadDisplayName = '';
        this.selectedFile = null;
        if (this.fileInput) this.fileInput.value = '';
        this.loadDiskSpace();
        this.loadVideos();
      } catch (e: unknown) {
        if (this.uploadCancelled) {
          const toast = await toastController.create({
            message: 'Загрузка отменена.',
            duration: 2000,
            position: 'top',
            color: 'medium',
          });
          await toast.present();
        } else {
          console.error('doUpload', e);
          const status = (e as { response?: { status?: number } })?.response?.status;
          const message = status === 400
            ? 'Ошибка загрузки: проверьте файл и размер (на сервере лимит 500 МБ)'
            : 'Ошибка загрузки видео';
          const toast = await toastController.create({
            message,
            duration: 4000,
            position: 'top',
            color: 'danger',
          });
          await toast.present();
        }
      } finally {
        this.uploadInProgress = false;
        this.uploadProgressPercent = 0;
        this.currentUploadId = null;
      }
    },
    async cancelUpload() {
      if (!this.currentUploadId) return;
      this.uploadCancelled = true;
      try {
        await this.adminService.abortChunkedUpload(this.currentUploadId);
        this.adminService.clearResumableUpload(this.currentUploadId);
      } catch (e) {
        console.error('cancelUpload', e);
      }
    },
    async renameVideo(video: IEducationVideo) {
      const alert = await alertController.create({
        header: 'Переименовать видео',
        cssClass: 'alert-dark-theme',
        inputs: [
          {
            name: 'displayName',
            type: 'text',
            value: video.displayName,
            placeholder: 'Новое название',
          },
        ],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Сохранить',
            handler: async (data) => {
              const newName = (data.displayName || '').trim();
              if (!newName) {
                const toast = await toastController.create({
                  message: 'Название не может быть пустым.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
                return false;
              }
              if (newName === video.displayName) {
                return true;
              }
              try {
                await this.adminService.updateEducationVideo(video.id, { displayName: newName });
                const toast = await toastController.create({
                  message: 'Название обновлено.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadVideos();
              } catch (e) {
                console.error('renameVideo', e);
                const toast = await toastController.create({
                  message: 'Ошибка при переименовании.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
    },
    async deleteVideo(video: IEducationVideo) {
      const alert = await alertController.create({
        header: 'Удалить видео?',
        message: `Удалить «${video.displayName}»? Файлы на сервере будут удалены.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.deleteEducationVideo(video.id);
                const toast = await toastController.create({
                  message: 'Видео удалено.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadVideos();
                this.loadDiskSpace();
              } catch (e) {
                console.error('deleteVideo', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления видео.',
                  duration: 3000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
    async cancelConversion(video: IEducationVideo) {
      const alert = await alertController.create({
        header: 'Отменить конвертацию?',
        message: `Остановить конвертацию «${video.displayName}»? Частично сконвертированный файл будет удалён.`,
        buttons: [
          { text: 'Нет', role: 'cancel' },
          {
            text: 'Отменить конвертацию',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.cancelConversion(video.id);
                const toast = await toastController.create({
                  message: 'Конвертация отменена.',
                  duration: 2000,
                  position: 'top',
                  color: 'warning',
                });
                await toast.present();
                this.loadVideos();
              } catch (e) {
                console.error('cancelConversion', e);
                const toast = await toastController.create({
                  message: 'Ошибка отмены конвертации.',
                  duration: 3000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
    async retryConversion(video: IEducationVideo) {
      try {
        await this.adminService.retryConversion(video.id);
        const toast = await toastController.create({
          message: 'Конвертация перезапущена.',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.loadVideos();
      } catch (e) {
        console.error('retryConversion', e);
        const errStatus = (e as { response?: { status?: number } })?.response?.status;
        let message = 'Ошибка перезапуска конвертации.';
        if (errStatus === 422) {
          message = 'Исходный файл (.upload) не найден. Перезапуск невозможен.';
        }
        const toast = await toastController.create({
          message,
          duration: 4000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--ion-color-dark);
}

.admin-card ion-card-title {
  color: white;
}

.admin-card ion-card-content {
  color: rgba(255, 255, 255, 0.85);
}

.admin-card ion-item,
.admin-card ion-list,
.admin-card ion-list ion-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: white;
}
.admin-card ion-list {
  background: transparent !important;
}
.admin-card ion-list ion-item::part(native),
.admin-card ion-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}

.disk-card p {
  margin: 0;
}

.file-info {
  margin: 8px 0;
}

.file-too-big {
  color: var(--ion-color-danger);
}

.file-chunked-hint {
  color: var(--ion-color-primary);
}

.upload-progress-text {
  margin: 4px 0 0;
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.85);
}

.upload-warning {
  margin: 4px 0 0;
  font-size: 0.9em;
  color: var(--ion-color-warning);
  font-weight: 600;
}

.info-hint {
  margin: 0 0 12px;
  font-size: 0.9em;
  color: var(--ion-color-primary);
}

.video-row ion-label h2 {
  color: white;
}

.video-row ion-label p {
  color: rgba(255, 255, 255, 0.7);
}

.video-row .conversion-progress {
  margin-top: 6px;
  height: 4px;
}

.video-row,
.admin-card .video-row {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
}
.video-row::part(native),
.admin-card .video-row::part(native) {
  --background: transparent !important;
  background: transparent !important;
}

.empty-hint {
  color: rgba(255, 255, 255, 0.6);
}

.video-actions {
  display: flex;
  gap: 4px;
  margin-left: 8px;
}

.video-actions ion-button {
  --padding-start: 4px;
  --padding-end: 4px;
}

.admin-video-embed {
  padding: 0 0 24px;
}

.admin-video-embed .admin-card {
  margin: 16px;
}

.upload-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

input[type="file"] {
  color: white;
}

.header-title {
  letter-spacing: 0.08em;
}

/* Кнопки в стиле приложения: чёрно-оранжевый */
.admin-card ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: white;
  --color-activated: white;
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="clear"] {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}
</style>
